import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { trpc, trpcClient } from '@/api/trpc';
import UserLayout from '@/components/Layout/UserLayout';
import { showToast } from '@/components/Layout/Toast';
import { Button, Card, EmptyState, FormSelect, Modal } from '@/components/UI';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import { CreateScorecardWizard } from '@/components/scorecards/CreateScorecardWizard';
import ShareResourceModal from '@/components/resourceAccess/ShareResourceModal';
import type { EntryScorecardsRow } from '@/types/appRouter';

const ScorecardsManager = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const initialStrategyId = Number(searchParams.get('strategyId')) || null;
    const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(initialStrategyId);
    const [showCreate, setShowCreate] = useState(false);
    const [scorecardToDelete, setScorecardToDelete] = useState<EntryScorecardsRow | null>(null);
    const [sharingScorecard, setSharingScorecard] = useState<EntryScorecardsRow | null>(null);
    const [deleting, setDeleting] = useState(false);

    const strategiesQuery = useQuery({
        ...trpc.strategy.fetchStrategies.queryOptions({ requireActive: false, ids: [] })
    });
    const strategies = useMemo(() => strategiesQuery.data?.strategies ?? [], [strategiesQuery.data]);

    useEffect(() => {
        if (strategies.length === 0 || strategies.some((strategy) => strategy.id === selectedStrategyId)) return;

        const strategyId = strategies[0]!.id;
        setSelectedStrategyId(strategyId);
        setSearchParams({ strategyId: String(strategyId) }, { replace: true });
    }, [selectedStrategyId, setSearchParams, strategies]);

    const scorecardsQuery = useQuery(trpc.scorecards.list.queryOptions());

    const scorecards = useMemo(() => scorecardsQuery.data ?? [], [scorecardsQuery.data]);
    const strategyNameById = useMemo(() => new Map(strategies.map((strategy) => [strategy.id, strategy.name])), [strategies]);
    const attachedStrategyIdsByScorecardId = useMemo(() => {
        const result = new Map<number, number[]>();

        for (const strategy of strategies) {
            const activeScorecardId = strategy.activeScorecardId ?? null;
            if (activeScorecardId === null) continue;
            const strategyIds = result.get(activeScorecardId) ?? [];
            strategyIds.push(strategy.id);
            result.set(activeScorecardId, strategyIds);
        }

        return result;
    }, [strategies]);

    const refresh = async () => {
        await queryClient.invalidateQueries({
            queryKey: trpc.scorecards.list.queryOptions().queryKey
        });
    };

    const deleteScorecard = async () => {
        if (!scorecardToDelete) return;
        setDeleting(true);
        try {
            await trpcClient.scorecards.delete.mutate({ scorecardId: scorecardToDelete.id });
            showToast('Scorecard deleted', 'success');
            setScorecardToDelete(null);
            await refresh();
        } catch (error) {
            showToast((error as Error).message || 'Failed to delete scorecard', 'error');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <UserLayout title="Scorecards">
            <Card className="p-4">
                <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                    <div className="min-w-72">
                        <FormSelect
                            id="scorecard-strategy-filter"
                            label="Training strategy for new scorecards"
                            options={[...strategies.map((strategy) => ({ value: String(strategy.id), label: strategy.name }))]}
                            value={selectedStrategyId?.toString() ?? ''}
                            onChange={(event) => {
                                const strategyId = event.target.value ? Number(event.target.value) : null;
                                setSelectedStrategyId(strategyId);
                                setSearchParams(strategyId ? { strategyId: String(strategyId) } : {});
                            }}
                        />
                    </div>
                    <Button disabled={selectedStrategyId === null} variant="primary" onClick={() => setShowCreate(true)}>
                        Create Scorecard
                    </Button>
                </div>

                {scorecardsQuery.isLoading ? (
                    <div className="py-8 text-center text-zinc-400">Loading scorecards…</div>
                ) : scorecards.length === 0 ? (
                    <EmptyState description={'Create a scorecard from a strategy experiment to get started.'} title="No scorecards" />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-zinc-700 text-left text-zinc-400">
                                <tr>
                                    <th className="px-3 py-2">Name</th>
                                    <th className="px-3 py-2">Training strategy</th>
                                    <th className="px-3 py-2">Attached to</th>
                                    <th className="px-3 py-2">Target</th>
                                    <th className="px-3 py-2">State</th>
                                    <th className="px-3 py-2">Updated</th>
                                    <th className="px-3 py-2 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scorecards.map((scorecard) => (
                                    <tr key={scorecard.id} className="border-b border-zinc-800">
                                        <td className="px-3 py-3">
                                            <Link className="text-blue-400 hover:underline" to={`/user/scorecard/${scorecard.id}`}>
                                                {scorecard.name}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-3 text-zinc-300">
                                            {scorecard.trainingStrategyId === null
                                                ? 'Unavailable'
                                                : (strategyNameById.get(scorecard.trainingStrategyId) ??
                                                  `#${scorecard.trainingStrategyId}`)}
                                        </td>
                                        <td className="px-3 py-3 text-zinc-300">
                                            {(() => {
                                                const strategyIds = attachedStrategyIdsByScorecardId.get(scorecard.id) ?? [];
                                                if (strategyIds.length === 0) return 'None';

                                                return strategyIds
                                                    .map((strategyId) => strategyNameById.get(strategyId) ?? `#${strategyId}`)
                                                    .join(', ');
                                            })()}
                                        </td>
                                        <td className="px-3 py-3 text-zinc-300">{scorecard.targetMetric}</td>
                                        <td className="px-3 py-3 text-zinc-300">
                                            {scorecard.initializationStatus === 'ready'
                                                ? scorecard.lockedAt === null
                                                    ? 'ready · editable'
                                                    : 'ready · locked'
                                                : scorecard.initializationStatus}
                                        </td>
                                        <td className="px-3 py-3 text-zinc-400">
                                            {scorecard.updatedAt ? dayjs(scorecard.updatedAt).format('YYYY-MM-DD HH:mm') : '—'}
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="flex justify-end gap-2">
                                                <Button size="sm" variant="secondary" onClick={() => setSharingScorecard(scorecard)}>
                                                    Share
                                                </Button>
                                                <Button size="sm" variant="danger" onClick={() => setScorecardToDelete(scorecard)}>
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <Modal isOpen={showCreate} title="Create Scorecard" onClose={() => setShowCreate(false)}>
                {selectedStrategyId !== null && (
                    <CreateScorecardWizard
                        strategyId={selectedStrategyId}
                        onCancel={() => setShowCreate(false)}
                        onComplete={(scorecardId) => {
                            setShowCreate(false);
                            void refresh();
                            window.location.href = `/user/scorecard/${scorecardId}?strategyId=${selectedStrategyId}`;
                        }}
                    />
                )}
            </Modal>

            {sharingScorecard && (
                <ShareResourceModal
                    isOpen
                    resourceLabel={sharingScorecard.name}
                    resourcePk={sharingScorecard.id}
                    resourceType="scorecard"
                    onClose={() => setSharingScorecard(null)}
                />
            )}

            <ConfirmationDialog
                confirmText={deleting ? 'Deleting…' : 'Delete'}
                confirmVariant="danger"
                isOpen={scorecardToDelete !== null}
                message="This permanently deletes the scorecard and its rules."
                title="Delete Scorecard"
                onCancel={() => !deleting && setScorecardToDelete(null)}
                onConfirm={() => void deleteScorecard()}
            />
        </UserLayout>
    );
};

export default ScorecardsManager;
